import React, { ReactNode, useEffect, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './msalConfig';
import { setAuthTokenGetter } from '../api/client';

interface AuthProviderProps {
  children: ReactNode;
}

let msalInstance: PublicClientApplication | null = null;

function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const instance = getMsalInstance();

    instance.initialize().then(() => {
      // Handle redirect promise on mount
      instance.handleRedirectPromise().then(() => {
        // Wire the axios client's token getter to MSAL so every API
        // request carries a fresh access token for our backend scope.
        setAuthTokenGetter(async () => {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) {
            throw new Error('No signed-in account available for token acquisition');
          }
          const request = { ...loginRequest, account: accounts[0] };
          try {
            const result = await instance.acquireTokenSilent(request);
            return result.accessToken;
          } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
              // Fall back to interactive redirect — caller will be navigated away.
              await instance.acquireTokenRedirect(request);
            }
            throw err;
          }
        });
        setIsInitialized(true);
      });
    });
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <MsalProvider instance={getMsalInstance()}>
      {children}
    </MsalProvider>
  );
};
